import { useEffect, useRef, useState, ReactNode } from "react";
import classNames from "classnames";

interface CtaConfig {
  label: string;
  href: string;
  variant: "primary" | "secondary";
  onClick?: () => void;
}

interface HeroSectionProps {
  backgroundImage: string;
  title: ReactNode;
  subtitle: ReactNode;
  primaryCta?: CtaConfig;
  secondaryCta?: CtaConfig;
  banner?: ReactNode;
  /** "centered" (default) keeps the original single-column hero.
   *  "split" renders copy on the left and `aside` on the right. */
  layout?: "centered" | "split";
  /** Optional eyebrow slot rendered above the title (split layout only). */
  chip?: ReactNode;
  /** Right-column content (e.g. a diagram) in split layout. */
  aside?: ReactNode;
}

export const HeroSection = ({
  backgroundImage,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  banner,
  layout = "centered",
  chip,
  aside,
}: HeroSectionProps) => {
  const [scrollY, setScrollY] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setRevealed(true);
      return;
    }

    // Small delay to ensure DOM is painted before reveal
    const timer = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          setScrollY(window.scrollY);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Shared progressive-reveal classes; `extra` carries spacing + stagger delay.
  const reveal = (extra: string) =>
    classNames(
      "transition-all duration-[280ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)]",
      extra,
      {
        "opacity-100 translate-y-0": revealed,
        "opacity-0 translate-y-[10px]": !revealed,
      }
    );

  const ctaButtons =
    primaryCta || secondaryCta ? (
      <>
        {primaryCta &&
          (primaryCta.onClick ? (
            <button
              onClick={primaryCta.onClick}
              className="w-full sm:w-auto text-center whitespace-nowrap bg-homeowners-purple text-white font-normal text-base lg:text-lg px-10 lg:px-12 py-4 lg:py-5 rounded-xl transition-all duration-[180ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:brightness-[0.94] hover:-translate-y-[2px] hover:shadow-lg active:translate-y-0 active:shadow-md"
            >
              {primaryCta.label}
            </button>
          ) : (
            <a
              href={primaryCta.href}
              className="w-full sm:w-auto text-center whitespace-nowrap bg-homeowners-purple text-white font-normal text-base lg:text-lg px-10 lg:px-12 py-4 lg:py-5 rounded-xl transition-all duration-[180ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:brightness-[0.94] hover:-translate-y-[2px] hover:shadow-lg active:translate-y-0 active:shadow-md"
            >
              {primaryCta.label}
            </a>
          ))}
        {secondaryCta && (
          <a
            href={secondaryCta.href}
            className="w-full sm:w-auto text-center whitespace-nowrap border border-homeowners-purple text-homeowners-purple font-normal text-base lg:text-lg px-10 lg:px-12 py-4 lg:py-5 rounded-xl transition-all duration-[180ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] hover:brightness-[0.94] hover:-translate-y-[2px] hover:shadow-lg hover:bg-homeowners-purple/5 active:translate-y-0 active:shadow-md"
          >
            {secondaryCta.label}
          </a>
        )}
      </>
    ) : null;

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex flex-col overflow-hidden"
    >
      {/* Background image with parallax */}
      <div
        className="absolute inset-0 z-0 will-change-transform"
        style={{ transform: `translate3d(0, ${scrollY * 0.3}px, 0)` }}
      >
        <img
          src={backgroundImage}
          alt="Luxury property background"
          className="w-full h-[120%] object-cover opacity-[0.15]"
        />
      </div>

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(to bottom, transparent 0%, rgba(248,248,246,0.5) 65%, #efefea 100%)",
        }}
      />

      {layout === "split" ? (
        /* ===== Split layout: copy + aside ===== */
        <div className="relative z-10 flex-1 flex flex-col px-6 sm:px-12 pt-20 sm:pt-28 lg:pt-32 xl:pt-20 pb-16 sm:pb-20 lg:pb-24">
          <div className="w-full flex-1 flex flex-col items-center text-center gap-10 xl:flex-row xl:items-center xl:justify-between xl:text-left xl:gap-6 xl:max-w-[1224px] xl:mx-auto">
            {/* Left — copy */}
            <div className="w-full flex flex-col items-center xl:items-start xl:max-w-[600px]">
              {chip && <div className={reveal("")}>{chip}</div>}
              <h1 className={reveal(chip ? "mt-5" : "")}>{title}</h1>
              <p className={reveal("mt-5 lg:mt-6 delay-[80ms]")}>{subtitle}</p>
              {ctaButtons && (
                <div
                  className={reveal(
                    "flex flex-col sm:flex-row items-center xl:items-start gap-4 mt-7 lg:mt-10 w-full sm:w-auto delay-[160ms]"
                  )}
                >
                  {ctaButtons}
                </div>
              )}
            </div>

            {/* Right — aside (diagram) */}
            {aside && (
              <div
                className={reveal(
                  "w-full xl:w-auto xl:flex-none flex justify-center overflow-visible delay-[240ms]"
                )}
              >
                {aside}
              </div>
            )}
          </div>

          {/* Banner — full width below the split row */}
          {banner && (
            <div
              className={reveal(
                "mt-12 sm:mt-16 lg:mt-20 w-full max-w-5xl mx-auto delay-[320ms]"
              )}
            >
              {banner}
            </div>
          )}
        </div>
      ) : (
        /* ===== Centered layout (original) ===== */
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-24 pt-20 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24">
          {/* Headline — progressive reveal (#2) */}
          <div className="text-center w-full">
            <h1 className={reveal("")}>{title}</h1>
            <p className={reveal("mt-6 lg:mt-8 delay-[80ms]")}>{subtitle}</p>
          </div>

          {/* CTA Buttons — progressive reveal + premium hover (#3) */}
          {ctaButtons && (
            <div
              className={reveal(
                "flex flex-col sm:flex-row items-center gap-4 sm:gap-5 mt-16 sm:mt-20 lg:mt-32 w-full sm:w-auto delay-[160ms]"
              )}
            >
              {ctaButtons}
            </div>
          )}

          {/* Banner — reveal */}
          {banner && (
            <div
              className={reveal(
                "mt-16 sm:mt-20 lg:mt-32 w-full max-w-5xl delay-[240ms]"
              )}
            >
              {banner}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
